import React from "react";
import "../ui/ui.scss";
import { resolveStatus } from "./statusVocabulary";

/**
 * StatusBadge — the one way a state is shown on screen.
 *
 * Contract: docs/design-system/TOKEN_CONTRACT.md §13
 * Vocabulary: ./statusVocabulary.js — the tone and label for every state.
 *
 *   <StatusBadge status={session.status} domain="demoSession" />
 *   <StatusBadge status={admin.status || "active"} />
 *   <StatusBadge status="new">3 unread</StatusBadge>
 *
 * ── WHAT CHANGED, AND WHY NOTHING MOVED ─────────────────────────────────────
 *
 * This used to interpolate the caller's string straight into a class name
 * (`ui-badge--${status}`) and render whatever CSS happened to exist for it. Two
 * things were wrong with that, both visible in the app:
 *
 *   1. A value with no matching rule — `success`, `default`, `live`, or any new
 *      enum the API grew — produced a pill with NO background, border or colour.
 *   2. `dot` rendered `<span class="ui-badge__dot" />`, and no stylesheet in the
 *      tree has ever defined that class. Every `dot={isLive}` in the app drew
 *      nothing.
 *
 * The class is now keyed by TONE, and the tone comes from the vocabulary, which
 * maps every word the app already passes to the tone it already rendered as. So
 * existing call sites look identical; the ones that were colourless get the
 * colour they were asking for.
 *
 * Props:
 *   status    — the state. An API enum (with `domain`) or a legacy tone word.
 *   domain    — a key of DOMAINS, to get that module's label and tone.
 *   children  — overrides the resolved label. Use it for a count or a phrase,
 *               not to re-word a state — that belongs in the vocabulary.
 *   dot       — show the leading dot. Automatic for a `live` tone.
 *   className — extra classes.
 */
const StatusBadge = ({
  status = "active",
  domain,
  children,
  className = "",
  dot = false,
  ...rest
}) => {
  const { label, tone, pulse } = resolveStatus(status, domain);

  // `outline` is a VARIANT, not a tone — a transparent chip with a border, used
  // where the badge must not compete with the row it sits in. It predates the
  // vocabulary and has its own rule, so it is passed through rather than
  // resolved. Anything else is one of the seven tones.
  const modifier = String(status).toLowerCase() === "outline" ? "outline" : tone;

  const classes = ["ui-badge", `ui-badge--${modifier}`, className]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes} {...rest}>
      {(dot || pulse) && (
        <span className={`ui-badge__dot${pulse ? " ui-badge__dot--pulse" : ""}`} />
      )}
      {children ?? label}
    </span>
  );
};

export default StatusBadge;
